import { useTranslation } from 'react-i18next';
import { Clock, Star, CheckCircle2, MessageSquarePlus, XCircle, Sparkles, PlusCircle, Target } from 'lucide-react';
import { useLang } from '@/contexts/LangContext';
import { useTheme } from '@/contexts/ThemeContext';

// Each shade has a light + dark variant. Dark variant uses deep slate
// surfaces so the white card text stays legible.
const SHADES_LIGHT = [
  { bg: 'linear-gradient(180deg, #FFFFFF 0%, #EEF2FF 100%)', accent: '#4F46E5', chip: 'bg-[#EEF2FF] text-[#4338CA]', ring: 'ring-1 ring-[#C7D2FE]' },
  { bg: 'linear-gradient(180deg, #FFFFFF 0%, #E0F7F4 100%)', accent: '#0EA5A4', chip: 'bg-[#CCFBF1] text-[#0F766E]', ring: 'ring-1 ring-[#99F6E4]' },
  { bg: 'linear-gradient(180deg, #FFFFFF 0%, #FFF7E0 100%)', accent: '#D97706', chip: 'bg-[#FEF3C7] text-[#92400E]', ring: 'ring-1 ring-[#FDE68A]' },
  { bg: 'linear-gradient(180deg, #FFFFFF 0%, #FFE4E6 100%)', accent: '#E11D48', chip: 'bg-[#FFE4E6] text-[#9F1239]', ring: 'ring-1 ring-[#FECDD3]' },
];
const SHADES_DARK = [
  { bg: 'linear-gradient(180deg, #1E293B 0%, #20284A 100%)', accent: '#818CF8', chip: 'bg-[#1E1B4B] text-[#C7D2FE]', ring: 'ring-1 ring-[#3730A3]' },
  { bg: 'linear-gradient(180deg, #1E293B 0%, #0F3D3A 100%)', accent: '#5EEAD4', chip: 'bg-[#063037] text-[#99F6E4]', ring: 'ring-1 ring-[#0F766E]' },
  { bg: 'linear-gradient(180deg, #1E293B 0%, #3D2A0A 100%)', accent: '#FBBF24', chip: 'bg-[#3A2E08] text-[#FDE68A]', ring: 'ring-1 ring-[#92400E]' },
  { bg: 'linear-gradient(180deg, #1E293B 0%, #3B0E1B 100%)', accent: '#FB7185', chip: 'bg-[#3B0A14] text-[#FECDD3]', ring: 'ring-1 ring-[#9F1239]' },
];

export default function TodaysMissionCard({
  mission,
  goal,
  index = 0,
  onComplete,
  onAddProgress,
  onNeedHelp,
  onAddDetails,
  onSkip,
}) {
  const { t: tt } = useTranslation();
  const { t } = useLang();
  const { resolvedTheme } = useTheme();
  const difficulty = mission.difficulty || 1;
  const impact = mission.impact_score || 3;
  const palette = resolvedTheme === 'dark' ? SHADES_DARK : SHADES_LIGHT;
  const shade = palette[index % palette.length];
  // Title/body text — light cards use dark text, dark cards use light text.
  const titleColor = resolvedTheme === 'dark' ? '#F1F5F9' : '#0F172A';
  const bodyColor = resolvedTheme === 'dark' ? '#CBD5E1' : '#4B4B4B';

  return (
    <div
      className={`gm-card p-6 relative overflow-hidden ${shade.ring}`}
      style={{ background: shade.bg, borderLeft: `6px solid ${shade.accent}` }}
      data-testid={`current-mission-card${index ? `-${index}` : ''}`}
      data-goal-id={goal?.id}
    >
      {/* Goal pill — shows which goal this mission belongs to */}
      {goal && (
        <div className="flex items-center justify-between mb-3 gap-2">
          <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider ${shade.chip}`}>
            <Target size={12} strokeWidth={3} />
            <span className="truncate max-w-[200px]" data-testid={`mission-card-goal-${goal.id}`}>{goal.title}</span>
          </div>
          {goal.is_primary && (
            <span className="text-[10px] font-extrabold text-[#FFC800] uppercase tracking-wider">{tt('c.dashboard.TodaysMissionCard.primary')}</span>
          )}
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase font-extrabold tracking-wider" style={{ color: shade.accent }}>
            {t('your_mission')}
          </p>
          <h2 className="text-xl sm:text-2xl font-extrabold mt-1 leading-tight" style={{ color: titleColor }} data-testid="mission-title">
            {mission.title}
          </h2>
        </div>
        <div className="bg-[#FFC800] text-[#4B4B4B] rounded-xl px-3 py-1 border-b-4 border-[#E5B400] shrink-0">
          <p className="text-xs font-extrabold">{tt('c.dashboard.TodaysMissionCard.reward_xp_xp', { reward_xp: mission.reward_xp })}</p>
        </div>
      </div>

      {mission.description && (
        <p className="text-base font-bold mt-3 leading-relaxed" style={{ color: bodyColor }}>
          {mission.description}
        </p>
      )}

      <div className="flex items-center gap-3 mt-4 text-sm text-[#AFAFAF] font-extrabold flex-wrap">
        <span className="flex items-center gap-1"><Clock size={16} strokeWidth={3} /> {tt('c.dashboard.TodaysMissionCard.estimated_minutes_min', { estimated_minutes: mission.estimated_minutes })}</span>
        <span>•</span>
        <span
          className={`px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wider ${
            difficulty <= 2
              ? 'bg-[#E8F8D8] text-[#16A34A]'
              : difficulty === 3
              ? 'bg-[#FFF7CC] text-[#B45309]'
              : 'bg-[#FEE2E2] text-[#DC2626]'
          }`}
          data-testid="mission-difficulty-pill"
        >
          {difficulty <= 2 ? tt('c.dashboard.TodaysMissionCard.easy') : difficulty === 3 ? tt('c.dashboard.TodaysMissionCard.medium') : tt('c.dashboard.TodaysMissionCard.hard')}
        </span>
        <span className="flex items-center gap-0.5" data-testid="mission-impact-stars" title={tt('c.dashboard.TodaysMissionCard.impact_score')}>
          <span className="text-[10px] uppercase mr-1">{tt('c.dashboard.TodaysMissionCard.impact')}</span>
          {[1, 2, 3, 4, 5].map((n) => (
            <Star
              key={n}
              size={12}
              strokeWidth={3}
              color={n <= impact ? '#FFC800' : '#E2E8F0'}
              fill={n <= impact ? '#FFC800' : 'none'}
            />
          ))}
        </span>
      </div>

      {mission.progress_notes?.length > 0 && (
        <div
          className="mt-3 p-2.5 rounded-xl border"
          style={{
            background: resolvedTheme === 'dark' ? 'rgba(15,23,42,0.55)' : 'rgba(255,255,255,0.6)',
            borderColor: resolvedTheme === 'dark' ? '#334155' : '#E2E8F0',
          }}
          data-testid="mission-progress-notes"
        >
          <p className="text-[10px] uppercase font-extrabold tracking-wider mb-1" style={{ color: resolvedTheme === 'dark' ? '#94A3B8' : '#94A3B8' }}> {tt('c.dashboard.TodaysMissionCard.your_progress_so_far')} </p>
          {mission.progress_notes.slice(-3).reverse().map((n, i) => (
            <p key={i} className="text-xs font-bold leading-snug" style={{ color: bodyColor }}>
              · {n.text}
            </p>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-2 mt-5">
        <button
          onClick={onComplete}
          className="gm-btn gm-btn-success w-full text-xl py-4"
          data-testid="open-complete-modal"
        >
          <CheckCircle2 size={24} strokeWidth={3} /> {t('mark_complete')}
        </button>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onAddProgress} className="gm-btn gm-btn-secondary py-3 text-sm" data-testid="open-add-progress">
            <PlusCircle size={18} strokeWidth={3} /> {tt('c.dashboard.TodaysMissionCard.add_progress')} </button>
          <button onClick={onNeedHelp} className="gm-btn gm-btn-secondary py-3 text-sm" data-testid="open-need-help">
            <Sparkles size={18} strokeWidth={3} /> {tt('c.dashboard.TodaysMissionCard.ask')} {goal.coach_type || tt('c.dashboard.TodaysMissionCard.coach')}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onAddDetails} className="gm-btn gm-btn-secondary py-3 text-sm" data-testid="open-refine-modal">
            <MessageSquarePlus size={18} strokeWidth={3} /> {tt('c.dashboard.TodaysMissionCard.add_details')} </button>
          <button onClick={onSkip} className="gm-btn gm-btn-secondary py-3 text-sm" data-testid="open-fail-modal">
            <XCircle size={18} strokeWidth={3} /> {tt('c.dashboard.TodaysMissionCard.skip')} </button>
        </div>
      </div>
    </div>
  );
}
